import { createContext, useEffect, useState, useCallback, use } from 'react';
import type { ReactNode } from 'react';
import { LazyDataLoader } from '@/lib/utils/lazy-data-loader';
import { DBCollection } from '@/lib/utils/constants-shared';

interface LiteraryFeaturesByPassageDataContextType {
    literaryFeaturesByPassage: LiteraryFeaturesByPassage[];
    getLiteraryFeaturesByPassageId: (passageId: string) => LiteraryFeaturesByPassage | undefined;
    loadLiteraryFeaturesByPassageId: (passageId: string) => Promise<LiteraryFeaturesByPassage | undefined>;
}

export const LiteraryFeaturesByPassageDataContext = createContext<LiteraryFeaturesByPassageDataContextType | undefined>(undefined);

interface LiteraryFeaturesByPassageDataProviderProps {
    children: ReactNode;
    literaryFeaturesByPassage: LiteraryFeaturesByPassage[];
}

export function LiteraryFeaturesByPassageDataProvider({ children, literaryFeaturesByPassage: initialData }: LiteraryFeaturesByPassageDataProviderProps) {
    const [loader] = useState(() => new LazyDataLoader<LiteraryFeaturesByPassage>(DBCollection.literaryFeaturesByPassage, 'passageId'));
    const [cachedItems, setCachedItems] = useState<LiteraryFeaturesByPassage[]>([]);

    useEffect(() => {
        if (initialData && initialData.length > 0) {
            loader.preload(initialData);
            setCachedItems(loader.getAllCached());
        }
    }, [initialData, loader]);

    const getLiteraryFeaturesByPassageId = useCallback((passageId: string): LiteraryFeaturesByPassage | undefined => {
        return cachedItems.find(item => item.passageId === passageId);
    }, [cachedItems]);

    const loadLiteraryFeaturesByPassageId = useCallback(async (passageId: string): Promise<LiteraryFeaturesByPassage | undefined> => {
        const item = await loader.get(passageId);
        if (item) {
            setCachedItems(loader.getAllCached());
        }
        return item;
    }, [loader]);

    return (
        <LiteraryFeaturesByPassageDataContext.Provider
            value={{
                literaryFeaturesByPassage: cachedItems,
                getLiteraryFeaturesByPassageId,
                loadLiteraryFeaturesByPassageId
            }}
        >
            {children}
        </LiteraryFeaturesByPassageDataContext.Provider>
    );
}

export function useLiteraryFeaturesByPassageData(passageId?: string) {
    const context = use(LiteraryFeaturesByPassageDataContext);
    const [data, setData] = useState<LiteraryFeaturesByPassage | undefined>();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!passageId || !context) return;

        const cached = context.getLiteraryFeaturesByPassageId(passageId);
        if (cached) {
            setData(cached);
            return;
        }

        setIsLoading(true);
        context.loadLiteraryFeaturesByPassageId(passageId)
            .then(loaded => {
                setData(loaded);
                setIsLoading(false);
            })
            .catch(err => {
                console.error(`Failed to load literary features by passage for ${passageId}:`, err);
                setIsLoading(false);
            });
    }, [passageId, context]);

    return { data, isLoading, context };
}

